import React, { useEffect } from "react"
import { Gesture, GestureDetector } from "~/ui/gesture-handler"
import Animated, { runOnJS, useSharedValue, withTiming, useAnimatedStyle, } from "~/ui/reanimated"
import styled, { useTheme } from "~/ui/styled"

import { StartFlex, CenterFlex, Text, IconFont, Image } from "~components/El"
import { useAudioRecorder } from "~context"
import useVoiceRecord from "~hooks/use-voice-record"
import { hapticTrigger } from "../../utils/haptic"

const noSelectStyle = `
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  -webkit-touch-callout: none;
`

const RecordingContainer = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 112px;
  padding: 10px 20px;
  box-sizing: border-box;
  touch-action: none;
  cursor: pointer;
  ${noSelectStyle}

  & * {
    ${noSelectStyle}
  }
`

const CancelAnchor = styled.div`
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  pointer-events: none;
`

const SLIDE_CANCEL_DISTANCE = -80

interface Props {
  onVoiceRecorded: ({ path, duration }: { path: string; duration: number }) => void
}

const VoiceRecordMode: React.FC<Props> = ({ onVoiceRecorded }) => {
  const theme = useTheme()

  const audioRecorderPlayer = useAudioRecorder()
  const {
    recording,
    setRecording,
    recordTime,
    onVoiceRecorderStart,
    onVoiceRecorderStop,
  } = useVoiceRecord({ onVoiceRecorded })

  /** ----------------------- Gesture State ----------------------- **/
  const pressAnim = useSharedValue(0) // 0=未按压, 1=按压中
  const cancelVisible = useSharedValue(0)
  const gestureX = useSharedValue(0)
  const isCancel = useSharedValue(false)
  const longPressed = useSharedValue(false)

  /** ----------------------- LongPress (开始录音) ----------------------- **/
  const longPressGesture = Gesture.LongPress()
    .minDuration(150) // 微信一样，避免误触
    .onStart(() => {
      longPressed.value = true
      pressAnim.value = 1
      cancelVisible.value = 1
      runOnJS(hapticTrigger)()
      runOnJS(setRecording)(true)
      runOnJS(onVoiceRecorderStart)()
    }).onEnd(() => {
      if (gestureX.value === 0) {
        runOnJS(onVoiceRecorderStop)(isCancel.value)
        longPressed.value = false
        gestureX.value = 0
        cancelVisible.value = 0
        pressAnim.value = 0
        isCancel.value = false
      }
      
    })

  /** ----------------------- Pan (滑动取消) ----------------------- **/
  const panGesture = Gesture.Pan()
    .onStart((e) => {
      gestureX.value = e.translationX
    })
    .onUpdate((e) => {
      if (!longPressed.value) return

      gestureX.value = e.translationX

      if (gestureX.value < SLIDE_CANCEL_DISTANCE) {
        if (!isCancel.value) {
          isCancel.value = true
          runOnJS(hapticTrigger)()
        }
      } else {
        isCancel.value = false
      }
    })
    .onFinalize(() => {
      if (longPressed.value) {
        runOnJS(onVoiceRecorderStop)(isCancel.value)
      }
      longPressed.value = false
      gestureX.value = 0
      cancelVisible.value = 0
      pressAnim.value = 0
      isCancel.value = false
    })

  /** ----------------------- Combine Gestures ----------------------- **/
  const composedGesture = Gesture.Simultaneous(panGesture, longPressGesture)

  const cancelAnimStyle = useAnimatedStyle(() => {
    return {
      opacity: withTiming(cancelVisible.value, { duration: 150 }),
      transform: [
        {
          translateY: withTiming(cancelVisible.value ? 0 : -10, { duration: 150 }),
        },
      ],
    }
  })

  const recordBtnStyle = useAnimatedStyle(() => {
    return {
      transform: [
        {
          scale: withTiming(pressAnim.value ? 1.15 : 1, { duration: 150 }),
        },
      ],
      opacity: withTiming(pressAnim.value ? 0.9 : 1, { duration: 150 }),
    }
  })

  /** ----------------------- Cleanup ----------------------- **/
  const init = async () => {
    await audioRecorderPlayer.stopRecorder()
    audioRecorderPlayer.removeRecordBackListener()
  }

  useEffect(() => {
    init()
  }, [])

  return (
    <RecordingContainer>
      {/* 左侧取消 UI — 相对录音区垂直居中 */}
      <CancelAnchor>
        <Animated.View style={cancelAnimStyle}>
          <StartFlex height="auto" width="auto" alignItems="center">
            <StartFlex height="auto" width="auto" flexDirection="column" alignItems="center">
              <Text color={theme.colors.infoText} fontSize="12px">
                左滑取消
              </Text>

              <CenterFlex
                width="56px"
                height="56px"
                mt="6px"
                borderRadius="28px"
                border={`1px solid ${theme.colors.error}`}
                bg="rgba(250, 82, 81, 0.2)"
              >
                <IconFont name={"Vector"} size={20} color={theme.colors.error} />
              </CenterFlex>
            </StartFlex>

            <CenterFlex padding="0 15px" alignSelf="center">
              <Image
                width="30px"
                height="8px"
                src={require("~assets/images/chat/voice-record-cancel.png")}
              />
            </CenterFlex>
          </StartFlex>
        </Animated.View>
      </CancelAnchor>

      {/* 主区域 — 水平垂直居中 */}
      <CenterFlex flexDirection="column" alignItems="center">
        <Text color={theme.colors.text} fontSize="14px" lineHeight="20px">
          {recording ? recordTime : "按住说话"}
        </Text>

        <GestureDetector gesture={composedGesture}>
          <Animated.View style={recordBtnStyle}>
            <CenterFlex
              width="80px"
              height="80px"
              mt="10px"
              borderRadius="40px"
              bg="rgba(7, 193, 96, 0.1)"
            >
              <IconFont name={"yy"} size={40} color={theme.colors.primary} />
            </CenterFlex>
          </Animated.View>
        </GestureDetector>
      </CenterFlex>
    </RecordingContainer>
  )
}

export default VoiceRecordMode
